/**
 * May this session list events? One answer, shared by every caller that reads
 * `GET /v1/api/me/events` WITHOUT the user having asked to see events.
 *
 * WHY. ERPat gates every event-data action on the `event` permit
 * (`Events_euapi::gate()` → `requirePermit('event')`, the same baseline the web
 * Events page enforces) and answers 403 `forbidden` without it. Roles that do
 * not grant it are ordinary — rs8's "Employee" role covers most of its staff —
 * yet three callers fetched the list regardless:
 *
 *   - the event-reminder planner, at every session start and every 15 minutes
 *     for as long as the app stays open;
 *   - the Timeline's upcoming-events strip, on every visit;
 *   - the Calendar's events source, on every month loaded.
 *
 * Each handled the 403 quietly, but each still sent it: a steady stream of
 * refused requests for a feature the account cannot use. The Events PAGE does
 * not go through here — someone who opens Events asks the server directly and
 * gets its calm "not available" state.
 *
 * HOW. The verdict comes from `GET /me/capabilities` (`canListEvents` — the same
 * guard map `requirePermit()` reads), which answers 200 either way. A 403/404
 * from a list call made through {@link whenEventsAllowed} also latches `denied`,
 * so a module that is switched off, or a capability map that ever drifts from
 * the gate, costs one refusal per window rather than one per refresh. When the
 * capabilities cannot be read at all (offline, a 5xx) nothing is remembered and
 * the caller goes ahead exactly as it did before this gate existed.
 *
 * Remembered per session identity — tenant + user + session epoch, so every
 * sign-out, expiry and company switch asks again — and for at most
 * {@link EVENTS_ACCESS_MAX_AGE_MS}: an installed PWA can stay signed in for days,
 * and a permit granted mid-session has to reach the reminder planner without a
 * sign-out. Re-asking costs one capabilities read, never a 403.
 */
import { ErpatApiError } from '@erpat/sdk';
import { capabilitiesService } from '@/services/erpat';
import { getSessionEpoch } from '@/services/api/erpat-client';
import { getSessionScope } from '@/services/session-scope';

/** How long a verdict is trusted before capabilities are read again. */
export const EVENTS_ACCESS_MAX_AGE_MS = 60 * 60 * 1000;

/** `unknown` = the capabilities could not be read; nothing was remembered. */
export type EventsAccess = 'granted' | 'denied' | 'unknown';

type Verdict = Exclude<EventsAccess, 'unknown'>;

let cached: { key: string; verdict: Verdict; at: number } | null = null;
let inFlight: { key: string; promise: Promise<EventsAccess> } | null = null;

/** Tenant + user + session epoch — see the module docblock. */
function accessKey(): string {
  const { companyKey, userId } = getSessionScope();
  return `${companyKey}|${userId}|${getSessionEpoch()}`;
}

/** Remember `verdict` for `key` — only while `key` is still the current session. */
function remember(key: string, verdict: Verdict): void {
  if (accessKey() === key) cached = { key, verdict, at: Date.now() };
}

/**
 * Is the remembered verdict for `key` still usable? A clock set backwards reads
 * as expired, so the worst a clock change can cost is one extra capabilities read.
 */
function isFresh(entry: typeof cached, key: string): entry is NonNullable<typeof cached> {
  if (!entry || entry.key !== key) return false;
  const age = Date.now() - entry.at;
  return age >= 0 && age < EVENTS_ACCESS_MAX_AGE_MS;
}

async function probe(key: string): Promise<EventsAccess> {
  try {
    const caps = await capabilitiesService.summary();
    const verdict: Verdict = caps.canListEvents ? 'granted' : 'denied';
    remember(key, verdict);
    return verdict;
  } catch {
    return 'unknown';
  }
}

/** The current session's verdict. Never rejects; concurrent callers share one read. */
export function eventsAccess(): Promise<EventsAccess> {
  const key = accessKey();
  if (isFresh(cached, key)) return Promise.resolve(cached.verdict);
  if (inFlight && inFlight.key === key) return inFlight.promise;

  // `.finally` runs on a later microtask, so the assignment below always lands
  // first and the cleanup can never clear a newer probe's slot.
  const promise = probe(key).finally(() => {
    if (inFlight?.promise === promise) inFlight = null;
  });
  inFlight = { key, promise };
  return promise;
}

/**
 * Run `load` — a `GET /me/events` list read — only if this session may list
 * events. Resolves `null` when it may not, without sending the request. A 403
 * or 404 from `load` is remembered as `denied` and then re-thrown, so the
 * caller's own error handling is unchanged.
 */
export async function whenEventsAllowed<T>(load: () => Promise<T>): Promise<T | null> {
  const key = accessKey();
  if ((await eventsAccess()) === 'denied') return null;
  try {
    return await load();
  } catch (err) {
    // `remember` re-checks the key, so a refusal that lands after a sign-out or
    // a company switch cannot deny the session that replaced it.
    if (err instanceof ErpatApiError && (err.status === 403 || err.status === 404)) {
      remember(key, 'denied');
    }
    throw err;
  }
}

/** Forget the remembered verdict. The session epoch already retires it at sign-out; this is for tests. */
export function forgetEventsAccess(): void {
  cached = null;
  inFlight = null;
}
